import React, { useEffect, useMemo, useRef, useState } from "react";
import cx from "classnames";
import styles from "./filters.module.scss";

export type LearnerStatusOption = {
  value: string;
  label: string;
};

interface StatusFilterProps {
  selected: LearnerStatusOption;
  options: LearnerStatusOption[];
  onChange: (option: LearnerStatusOption) => void;
}

export const StatusFilter: React.FC<StatusFilterProps> = ({
  selected,
  options,
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const defaultOption = options[0];

  const toggleLabel = useMemo(() => {
    if (selected.value === "ALL") {
      return "all status";
    }

    return selected.label.toLowerCase();
  }, [selected]);

  const hasSelection = selected.value !== "ALL";

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleChange = (option: LearnerStatusOption) => {
    onChange(option);
    setOpen(false);
  };

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (defaultOption) {
      onChange(defaultOption);
    }
    setOpen(false);
  };

  return (
    <div className={styles.filterDropdown} ref={ref}>
      <button
        type="button"
        className={cx(styles.toggle, { [styles.isOpen]: open })}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className={styles.label}>{toggleLabel}</span>
        {hasSelection && (
          <i
            className={cx("paperflite-close", styles.clearIcon)}
            onClick={handleClear}
          />
        )}
        <i className={cx("paperflite-dropdown", styles.chevron)} />
      </button>

      {open && (
        <div className={styles.menu}>
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              className={cx(styles.menuItem, {
                [styles.active]: option.value === selected.value,
              })}
              onClick={() => handleChange(option)}
            >
              {option.value === "ALL" ? "all status" : option.label.toLowerCase()}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
